/**
 * PhotoScreen.tsx
 * @version 3.0.0 — Map-based : schéma VP/VU cliquable, libre navigation
 */
import React, { useState, useRef, useEffect, useCallback, memo } from 'react'
import { Camera, X, CheckCircle2, ZoomIn, ChevronLeft } from 'lucide-react'
import { useAppStore }        from '../store/useAppStore'
import { useSettingsStore }   from '../store/useSettingsStore'
import { usePhotoSlotsStore } from '../store/usePhotoSlotsStore'
import { photoMemory }        from '../store/photoMemoryStore'
import Header                 from '../components/ui/Header'
import AnnotationCanvas       from '../components/annotation/AnnotationCanvas'
import { VehicleMapView }     from '../components/VehicleMapSchematic'
import { useGeolocation, formatCoords, reverseGeocode } from '../utils/geolocation'
import toast from 'react-hot-toast'

// ── Types ─────────────────────────────────────────────────────────────────────
interface PhotoMetadata { timestamp: string; location: string }

// ── Constantes watermark ──────────────────────────────────────────────────────
const META_BAND_RATIO  = 0.08
const META_BAND_MIN_PX = 56
const META_BAND_MAX_PX = 96

function computeBandHeight(imgH: number) {
  return Math.max(META_BAND_MIN_PX, Math.min(META_BAND_MAX_PX, Math.round(imgH * META_BAND_RATIO)))
}

function drawMetadataBand(ctx: CanvasRenderingContext2D, imgW: number, imgH: number, bandH: number, meta: PhotoMetadata) {
  ctx.fillStyle = '#111827'; ctx.fillRect(0, imgH, imgW, bandH)
  ctx.fillStyle = '#374151'; ctx.fillRect(0, imgH, imgW, 2)
  const px = Math.max(10, imgW * 0.015)
  const lg = bandH * 0.5
  ctx.font = `bold ${Math.max(11, Math.min(bandH * 0.28, 20))}px sans-serif`
  ctx.textBaseline = 'middle'; ctx.fillStyle = '#f9fafb'; ctx.textAlign = 'left'
  ctx.fillText(`🕐 ${meta.timestamp}`, px, imgH + lg * 0.5)
  ctx.font = `${Math.max(10, Math.min(bandH * 0.25, 17))}px sans-serif`
  ctx.fillStyle = meta.location.includes('indisponible') ? '#f87171' : '#93c5fd'
  let t = `📍 ${meta.location}`
  while (ctx.measureText(t).width > imgW * 0.9 && t.length > 15) t = t.slice(0, -4) + '…'
  ctx.fillText(t, px, imgH + lg * 1.5)
}

async function compressForMRC(file: File, quality = 0.82, meta?: PhotoMetadata): Promise<string> {
  const url = URL.createObjectURL(file)
  return new Promise((res, rej) => {
    const img = new window.Image()
    img.onerror = () => { URL.revokeObjectURL(url); rej(new Error('Image invalide')) }
    img.onload  = () => {
      URL.revokeObjectURL(url)
      const bH = meta ? computeBandHeight(img.naturalHeight) : 0
      const c  = Object.assign(document.createElement('canvas'), { width: img.naturalWidth, height: img.naturalHeight + bH })
      const ctx = c.getContext('2d')!
      ctx.drawImage(img, 0, 0)
      if (meta) drawMetadataBand(ctx, img.naturalWidth, img.naturalHeight, bH, meta)
      res(c.toDataURL('image/jpeg', quality))
      c.width = c.height = 0
    }
    img.src = url
  })
}

async function prepareAnnotationImage(file: File, quality = 0.65, maxDim = 2048, meta?: PhotoMetadata): Promise<string> {
  const url = URL.createObjectURL(file)
  return new Promise((res, rej) => {
    const img = new window.Image()
    img.onerror = () => { URL.revokeObjectURL(url); rej(new Error('Image invalide')) }
    img.onload  = () => {
      URL.revokeObjectURL(url)
      const r = Math.min(maxDim / img.naturalWidth, maxDim / img.naturalHeight, 1)
      const w = Math.round(img.naturalWidth * r), h = Math.round(img.naturalHeight * r)
      const bH = meta ? computeBandHeight(h) : 0
      const c  = Object.assign(document.createElement('canvas'), { width: w, height: h + bH })
      const ctx = c.getContext('2d')!
      ctx.drawImage(img, 0, 0, w, h)
      if (meta) drawMetadataBand(ctx, w, h, bH, meta)
      res(c.toDataURL('image/jpeg', quality))
      c.width = c.height = 0
    }
    img.src = url
  })
}

function extractSrc(photo: any): string | null {
  if (!photo) return null
  if (typeof photo === 'string') return photo
  if (photo.dataUrl) return photo.dataUrl
  return null
}

// ── Lightbox ──────────────────────────────────────────────────────────────────
const Lightbox = memo(({ src, label, onClose }: { src: string; label: string; onClose: () => void }) => {
  const [scale, setScale] = useState(1)
  const [pos,   setPos]   = useState({ x: 0, y: 0 })
  const imgRef     = useRef<HTMLImageElement>(null)
  const lastTapRef = useRef(0)
  const lastDist   = useRef<number | null>(null)
  const lastPan    = useRef<{ x: number; y: number } | null>(null)
  const dragging   = useRef(false)

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h)
  }, [onClose])

  const clamp = (tx: number, ty: number, sc: number) => {
    const img = imgRef.current; if (!img) return { x: tx, y: ty }
    const mx = Math.max(0, img.clientWidth  * (sc - 1) / 2)
    const my = Math.max(0, img.clientHeight * (sc - 1) / 2)
    return { x: Math.min(mx, Math.max(-mx, tx)), y: Math.min(my, Math.max(-my, ty)) }
  }

  const onTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      lastDist.current = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY)
      lastPan.current  = null
    } else { lastPan.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; dragging.current = false }
  }
  const onTouchMove = (e: React.TouchEvent) => {
    e.preventDefault()
    if (e.touches.length === 2) {
      const d = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY)
      if (lastDist.current) setScale(p => Math.min(5, Math.max(1, p * d / lastDist.current!)))
      lastDist.current = d
    } else if (e.touches.length === 1 && lastPan.current) {
      setScale(p => {
        if (p <= 1) return p
        const dx = e.touches[0].clientX - lastPan.current!.x
        const dy = e.touches[0].clientY - lastPan.current!.y
        if (Math.abs(dx) > 2 || Math.abs(dy) > 2) dragging.current = true
        setPos(t => clamp(t.x + dx, t.y + dy, p))
        lastPan.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }
        return p
      })
    }
  }
  const onTouchEnd = () => { lastDist.current = null; lastPan.current = null }
  const onTap = (e: React.TouchEvent | React.MouseEvent) => {
    if (dragging.current) { dragging.current = false; return }
    const now = Date.now()
    if (now - lastTapRef.current < 300) {
      setScale(p => { if (p > 1) { setPos({ x: 0, y: 0 }); return 1 }; return 2.5 })
    }
    lastTapRef.current = now
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center" onClick={() => { if (scale <= 1) onClose() }}>
      <button onClick={e => { e.stopPropagation(); onClose() }} className="absolute top-4 right-4 bg-white/20 text-white rounded-full p-2 z-10"><X size={24}/></button>
      {label && <p className="absolute top-4 left-1/2 -translate-x-1/2 text-white text-sm font-semibold bg-black/60 px-4 py-1 rounded-full z-10">{label}</p>}
      <div className="w-full h-full flex items-center justify-center overflow-hidden" onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd} onClick={onTap} style={{ touchAction: 'none' }}>
        <img ref={imgRef} src={src} alt={label} draggable={false}
          className="max-w-full max-h-[90vh] object-contain rounded-xl select-none pointer-events-none"
          style={{ transform: `translate(${pos.x}px,${pos.y}px) scale(${scale})`, transformOrigin: 'center', transition: scale === 1 ? 'transform 0.2s' : 'none' }} />
      </div>
      {scale > 1 && <button onClick={e => { e.stopPropagation(); setScale(1); setPos({ x: 0, y: 0 }) }} className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white text-xs bg-black/70 px-4 py-1.5 rounded-full border border-white/20 z-10">{scale.toFixed(1)}x — Tap pour réinitialiser</button>}
    </div>
  )
})
Lightbox.displayName = 'Lightbox'

// ── ComparisonPanel (retour : départ vs retour côte à côte) ───────────────────
const ComparisonPanel = memo(({
  slotId, label, onClose, onRetake,
}: { slotId: string; label: string; onClose: () => void; onRetake: () => void }) => {
  const [lb, setLb] = useState<'depart'|'retour'|null>(null)
  const departSrc = extractSrc(photoMemory.getDepartPhoto(slotId))
  const retourSrc = extractSrc(photoMemory.getPhoto(slotId))

  return (
    <div className="fixed inset-0 z-40 bg-black/90 flex flex-col">
      <div className="flex items-center justify-between px-4 pt-4 pb-2">
        <button onClick={onClose} className="flex items-center gap-1 text-gray-400 text-sm"><ChevronLeft size={18}/>Retour</button>
        <p className="text-white font-semibold text-sm">{label}</p>
        <button onClick={onRetake} className="flex items-center gap-1 text-blue-400 text-sm"><Camera size={15}/>Reprendre</button>
      </div>
      <p className="text-center text-[10px] text-blue-400 font-bold uppercase tracking-widest mb-2">Comparaison Départ / Retour</p>
      <div className="flex-1 grid grid-cols-2 gap-3 px-3 pb-4">
        {[
          { src: departSrc, key: 'depart' as const, colorBorder: 'border-green-400', colorText: 'text-green-400', title: 'Départ' },
          { src: retourSrc, key: 'retour' as const, colorBorder: 'border-orange-400', colorText: 'text-orange-400', title: 'Retour' },
        ].map(col => (
          <div key={col.key} className="flex flex-col gap-1">
            <p className={`text-[10px] font-bold uppercase text-center ${col.colorText}`}>{col.title}</p>
            {col.src ? (
              <button onClick={() => setLb(col.key)} className={`flex-1 rounded-xl overflow-hidden border-2 ${col.colorBorder} bg-gray-800 active:scale-95`}>
                <img src={col.src} alt={col.title} className="w-full h-full object-cover" />
              </button>
            ) : (
              <div className={`flex-1 rounded-xl border-2 border-dashed ${col.colorBorder} bg-gray-900 flex items-center justify-center text-xs text-gray-500`}>
                {col.key === 'depart' ? 'Non disponible' : 'À prendre'}
              </div>
            )}
          </div>
        ))}
      </div>
      {lb && (
        <Lightbox
          src={lb === 'depart' ? departSrc! : retourSrc!}
          label={`${lb === 'depart' ? 'Départ' : 'Retour'} · ${label}`}
          onClose={() => setLb(null)}
        />
      )}
    </div>
  )
})
ComparisonPanel.displayName = 'ComparisonPanel'

// ── Composant principal ───────────────────────────────────────────────────────
export default function PhotoScreen() {
  const inspection        = useAppStore(s => s.inspection)
  const _photoVersion     = useAppStore(s => s._photoVersion)
  const setPhoto          = useAppStore(s => s.setPhoto)
  const setStep           = useAppStore(s => s.setStep)
  const setPendingPhoto   = useAppStore(s => s.setPendingPhoto)
  const clearPendingPhoto = useAppStore(s => s.clearPendingPhoto)
  const getSetting        = useSettingsStore(s => s.getSetting)
  const photoSlots        = usePhotoSlotsStore(s => s.photoSlots)

  const { position: geoPosition, address: geoAddress } = useGeolocation()

  const [activeSlotId,    setActiveSlotId]    = useState<string | null>(null)
  const [annotationImage, setAnnotationImage] = useState<string | null>(null)
  const [isProcessing,    setIsProcessing]    = useState(false)
  const [comparisonSlot,  setComparisonSlot]  = useState<string | null>(null)

  const fileInputRef      = useRef<HTMLInputElement>(null)
  const originalBase64Ref = useRef<string | null>(null)

  const activeSlot  = photoSlots.find(s => s.id === activeSlotId) ?? null
  const isAnnotating = !!annotationImage

  // Détecter type véhicule VP/VU
  const vehicleType: 'VP' | 'VU' =
    (inspection?.contract as any)?.vehicleType === 'VU' ? 'VU' : 'VP'

  // Restauration brouillon
  useEffect(() => {
    const { pendingPhotoSlot, pendingPhotoData } = inspection || {}
    if (!pendingPhotoSlot || !pendingPhotoData) return
    setActiveSlotId(pendingPhotoSlot)
    setAnnotationImage(pendingPhotoData)
    setIsProcessing(false)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Compteur photos
  const doneCount  = Object.keys(photoMemory.getPhotos()).length
  const totalCount = photoSlots.length
  const allDone    = doneCount >= totalCount

  const triggerCamera = useCallback((slotId: string) => {
    if (!fileInputRef.current) return
    setActiveSlotId(slotId)
    fileInputRef.current.value = ''
    setIsProcessing(true)

    let cancelled = false
    const cleanup = () => {
      cancelled = true
      window.removeEventListener('focus', onReturn)
      document.removeEventListener('visibilitychange', onVis)
      clearTimeout(safety)
    }
    const check = () => {
      if (cancelled) return
      cleanup()
      setTimeout(() => {
        if (fileInputRef.current && (!fileInputRef.current.files || fileInputRef.current.files.length === 0)) {
          setIsProcessing(false)
        }
      }, 800)
    }
    const onReturn = () => check()
    const onVis    = () => { if (document.visibilityState === 'visible') check() }
    window.addEventListener('focus', onReturn)
    document.addEventListener('visibilitychange', onVis)
    const safety = setTimeout(() => { if (!cancelled) { cleanup(); setIsProcessing(false) } }, 10000)

    fileInputRef.current.click()
  }, [])

  const handleNativeCapture = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !activeSlotId) { setIsProcessing(false); return }
    const slot = photoSlots.find(s => s.id === activeSlotId)
    if (!slot) { setIsProcessing(false); return }
    try {
      const mrcQ = getSetting('photo_mrc_quality')
      const supQ = getSetting('photo_supabase_quality')
      const supD = getSetting('photo_supabase_max_dim')

      const now = new Date()
      const timestamp = now.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
      let location = 'GPS indisponible'
      if (geoPosition) {
        if (geoAddress) { location = geoAddress }
        else {
          const r = await Promise.race([
            reverseGeocode(geoPosition.lat, geoPosition.lng),
            new Promise<null>(res => setTimeout(() => res(null), 3000)),
          ])
          location = r || formatCoords(geoPosition.lat, geoPosition.lng)
        }
      }
      const meta: PhotoMetadata = { timestamp, location }
      const mrcB64 = await compressForMRC(file, mrcQ, meta)
      const annUrl = await prepareAnnotationImage(file, supQ, supD, meta)
      originalBase64Ref.current = mrcB64 as string
      setPendingPhoto(activeSlotId, annUrl)
      setAnnotationImage(annUrl as string)
    } catch (err: any) {
      toast.error(err?.message || 'Erreur traitement photo')
    } finally {
      setIsProcessing(false)
    }
  }, [activeSlotId, photoSlots, getSetting, geoPosition, geoAddress, setPendingPhoto])

  const validatePhoto = useCallback((annotatedUrl: string | null) => {
    if (!activeSlotId) return
    if (navigator.vibrate) navigator.vibrate([20])
    setPhoto(activeSlotId, annotatedUrl || annotationImage, originalBase64Ref.current)
    clearPendingPhoto()
    setAnnotationImage(null)
    originalBase64Ref.current = null
    setActiveSlotId(null)
  }, [activeSlotId, annotationImage, setPhoto, clearPendingPhoto])

  const handleRetake = useCallback(() => {
    clearPendingPhoto()
    setAnnotationImage(null)
    originalBase64Ref.current = null
    if (activeSlotId) triggerCamera(activeSlotId)
  }, [clearPendingPhoto, activeSlotId, triggerCamera])

  const handleThumbTap = useCallback((slotId: string) => {
    // Mode retour : ouvrir le panneau de comparaison
    if (inspection?.type === 'retour') {
      setComparisonSlot(slotId)
    } else {
      // Mode départ : retake direct
      triggerCamera(slotId)
    }
  }, [inspection?.type, triggerCamera])

  const handleFinish = useCallback(() => {
    if (navigator.vibrate) navigator.vibrate([20])
    setStep('fuel')
    toast.success('Photos terminées !')
  }, [setStep])

  // Guard chargement slots
  if (photoSlots.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-950">
        <div className="flex flex-col items-center gap-3 text-gray-400">
          <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"/>
          <p className="text-sm">Chargement des zones photo…</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-950" style={{ height: '100dvh', overflow: 'hidden' }}>
      <input ref={fileInputRef} type="file" accept="image/*" capture="environment"
        className="hidden" onChange={handleNativeCapture} aria-hidden="true" />

      {/* Overlay traitement */}
      {isProcessing && (
        <div className="fixed inset-0 z-50 bg-black/80 flex flex-col items-center justify-center gap-4">
          <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin"/>
          <p className="text-white text-sm font-semibold">Traitement de la photo…</p>
          <button onClick={() => setIsProcessing(false)} className="mt-4 text-gray-400 text-xs underline">Annuler</button>
        </div>
      )}

      {/* Mode annotation */}
      {isAnnotating && activeSlot && (
        <AnnotationCanvas
          imageDataUrl={annotationImage}
          slotId={activeSlot.id}
          slotLabel={activeSlot.label}
          hasPrev={false}
          hasNext={false}
          onPrev={() => {}}
          onNext={() => {}}
          onValidate={validatePhoto}
          onRetake={handleRetake}
        />
      )}

      {/* Panneau comparaison retour */}
      {comparisonSlot && !isAnnotating && (
        <ComparisonPanel
          slotId={comparisonSlot}
          label={photoSlots.find(s => s.id === comparisonSlot)?.label ?? comparisonSlot}
          onClose={() => setComparisonSlot(null)}
          onRetake={() => { setComparisonSlot(null); triggerCamera(comparisonSlot) }}
        />
      )}

      {/* Vue principale — schéma */}
      {!isAnnotating && !comparisonSlot && (
        <>
          <Header
            title={`Photos · ${vehicleType}`}
            subtitle={`${doneCount} / ${totalCount} photos`}
            showBack
            backStep="recap"
          />

          {/* Barre de progression */}
          <div className="flex-shrink-0 px-4 py-2 bg-gray-900">
            <div className="flex gap-1">
              {photoSlots.map(sl => {
                const done = !!photoMemory.getPhoto(sl.id)
                return (
                  <div key={sl.id}
                    className={`flex-1 rounded-full transition-all duration-300 ${done ? 'bg-green-400' : 'bg-gray-700'}`}
                    style={{ height: 5 }}
                  />
                )
              })}
            </div>
            <p className="text-[10px] text-gray-500 text-right mt-1">{doneCount}/{totalCount} photos</p>
          </div>

          {/* Schéma véhicule */}
          <div className="flex-1 min-h-0 overflow-hidden flex flex-col items-center justify-center px-2 pb-[80px]">
            <div className="w-full h-full max-w-sm flex items-center justify-center">
              <VehicleMapView
                vehicleType={vehicleType}
                slots={photoSlots}
                photoVersion={_photoVersion ?? 0}
                onTapSlot={triggerCamera}
                onThumbSlot={handleThumbTap}
                isRetour={inspection?.type === 'retour'}
              />
            </div>

            {/* Légende */}
            <div className="flex gap-4 mt-4 text-[10px] text-gray-500">
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-amber-400 inline-block"/>À photographier</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-green-500 inline-block"/>Fait</span>
            </div>
          </div>

          {/* Bouton Terminer fixe en bas */}
          <div className="fixed bottom-0 left-0 right-0 px-4 py-4 bg-gray-950 border-t border-gray-800">
            {allDone ? (
              <button
                onClick={handleFinish}
                className="w-full bg-green-600 active:bg-green-700 text-white font-bold py-4 rounded-2xl text-base shadow-lg border-b-4 border-green-800 flex items-center justify-center gap-2 active:scale-95 transition-all"
              >
                <CheckCircle2 size={22}/>Terminer les photos ➡️
              </button>
            ) : (
              <button
                onClick={handleFinish}
                className="w-full bg-gray-800 active:bg-gray-700 text-gray-400 font-semibold py-4 rounded-2xl text-sm flex items-center justify-center gap-2"
              >
                Ignorer et continuer ({totalCount - doneCount} restantes) ➡️
              </button>
            )}
          </div>
        </>
      )}

      {/* Pulse animation CSS */}
      <style>{`
        @keyframes pulse-amber {
          0%,100% { box-shadow: 0 0 0 0 rgba(251,191,36,0.5); }
          50%      { box-shadow: 0 0 0 8px rgba(251,191,36,0); }
        }
      `}</style>
    </div>
  )
}